import React, { useState, useEffect } from 'react';
import { User, Upload, Trash2 } from 'lucide-react';
import PositionSelector from './PositionSelector';

// Lista de bancos dominicanos
const BANCOS_DOMINICANOS = [
  'BHD León',
  'Banreservas',
  'Banco Popular',
  'Banco Santa Cruz',
  'Asociación La Nacional de Ahorros y Préstamos',
  'Banco BDI',
  'Banco Promerica',
  'Scotiabank',
  'Citibank',
  'Banco Ademi',
  'Banco Caribe',
  'Banco López de Haro',
  'Banco Múltiple VIMENCA'
];

const TIPOS_SANGRE = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const TIPOS_NOMINA = [
  'Mensual',
  'Quincenal',
  'Semanal',
  'Bonificación',
  'Comisiones',
  'Salario de Navidad'
];

const ESTADOS_EMPLEADO = [
  'Activo',
  'Período de Prueba',
  'Suspendido',
  'Vacaciones',
  'Licencia Médica',
  'Licencia sin Sueldo'
];

const TIPOS_EMPLEADO = [
  'Tiempo Completo',
  'Medio Tiempo',
  'Por Contrato',
  'Temporal',
  'Pasante',
  'Freelance'
];

const DIAS_SEMANA = [
  { value: 'lunes', label: 'Lunes' },
  { value: 'martes', label: 'Martes' },
  { value: 'miercoles', label: 'Miércoles' },
  { value: 'jueves', label: 'Jueves' },
  { value: 'viernes', label: 'Viernes' },
  { value: 'sabado', label: 'Sábado' },
  { value: 'domingo', label: 'Domingo' }
];

// ✅ Función helper para convertir fecha DD/MM/YYYY a YYYY-MM-DD
const parseDisplayDateToISO = (displayDate) => {
  if (!displayDate) return '';
  
  // Si ya está en formato ISO (YYYY-MM-DD)
  if (displayDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return displayDate;
  }
  
  // Si está en formato DD/MM/YYYY
  if (displayDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    const [day, month, year] = displayDate.split('/');
    return `${year}-${month}-${day}`;
  }
  
  // Intentar parsear como Date
  try {
    const date = new Date(displayDate);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch (e) {
    console.error('Error parseando fecha:', displayDate, e);
  }
  
  return '';
};

export default function EmployeeForm({ employee, departments, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    // Información Personal
    nombre: '',
    apellido: '',
    cedula: '',
    fechaNacimiento: '',
    edad: '',
    sexo: '',
    nacionalidad: 'Dominicana',
    pais: 'República Dominicana',
    tipoSangre: '',
    profesion: '',
    telefono: '',
    celular: '',
    email: '',
    direccion: '',
    nss: '',
    nivelEducativo: '',
    contactoEmergencia: '',
    foto: '',

    // Información Bancaria
    nombreBanco: '',
    cuentaBanco: '',

    // Estado
    estado: 'activo'
  });


  useEffect(() => {
    if (employee) {
      console.log('📥 Datos del empleado recibidos:', employee);
      
      // ✅ CORRECCIÓN CRÍTICA: Parsear fechas correctamente
      let fechaNacimiento = '';
      let fechaIngreso = '';

      // Intentar múltiples formatos para fecha_nacimiento
      if (employee.fecha_nacimiento || employee.fechaNacimiento || employee.fechaNacimientoRaw || employee.fecha_nacimiento_raw) {
        const rawDate = employee.fecha_nacimiento_raw || employee.fechaNacimientoRaw || employee.fecha_nacimiento || employee.fechaNacimiento;
        fechaNacimiento = parseDisplayDateToISO(rawDate);
        console.log('📅 Fecha Nacimiento parseada:', rawDate, '→', fechaNacimiento);
      }

      // Intentar múltiples formatos para fecha_ingreso
      if (employee.fecha_ingreso || employee.fechaIngreso || employee.fechaIngresoRaw || employee.fecha_ingreso_raw) {
        const rawDate = employee.fecha_ingreso_raw || employee.fechaIngresoRaw || employee.fecha_ingreso || employee.fechaIngreso;
        fechaIngreso = parseDisplayDateToISO(rawDate);
        console.log('📅 Fecha Ingreso parseada:', rawDate, '→', fechaIngreso);
      }

      const formattedEmployee = {
        ...employee,
        salario: employee.salario ?
          parseFloat(employee.salario.toString().replace(/,/g, '')).toLocaleString('en-US') : '',
        // ✅ Usar fechas parseadas
        fechaNacimiento: fechaNacimiento,
        fechaIngreso: fechaIngreso,
        foto: employee.foto || '',
        fotoPreview: employee.foto || '',
        departmentId: employee.departmentId || employee.department_id || '',
        positionId: employee.positionId || employee.position_id || '',
        nacionalidad: employee.nacionalidad || 'Dominicana',
        pais: employee.pais || 'República Dominicana',
        tipoNomina: employee.tipoNomina || employee.tipo_nomina || 'Mensual',
        estadoEmpleado: employee.estadoEmpleado || employee.estado_empleado || 'Activo',
        tipoEmpleado: employee.tipoEmpleado || employee.tipo_empleado || 'Tiempo Completo'
      };
      
      console.log('📝 FormData a setear:', {
        fechaNacimiento: formattedEmployee.fechaNacimiento,
        fechaIngreso: formattedEmployee.fechaIngreso
      });
      
      setFormData(formattedEmployee);
    }
  }, [employee]);

  const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };

    if (name === 'fechaNacimiento' && value) {
      newData.edad = calculateAge(value);
      console.log('📅 Fecha nacimiento cambiada a:', value);
    }

    if (name === 'fechaIngreso' && value) {
      console.log('📅 Fecha ingreso cambiada a:', value);
    }

    setFormData(newData);
  };

  const handleSalarioChange = (e) => {
    const value = e.target.value;
    const cleanValue = value.replace(/[^\d.]/g, '');

    let formatted = cleanValue;
    if (cleanValue) {
      const parts = cleanValue.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      formatted = parts.join('.');
    }

    setFormData({ ...formData, salario: formatted });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Por favor selecciona un archivo de imagen');
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        alert('La imagen no debe superar 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ 
          ...prev, 
          foto: file,
          fotoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFoto = () => {
    setFormData(prev => ({ ...prev, foto: '', fotoPreview: '' }));
  };


  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.nombre || !formData.apellido || !formData.cedula) {
      alert('Por favor complete los campos obligatorios: Nombre, Apellido y Cédula');
      return;
    }

    // ✅ CORRECCIÓN: Enviar fechas en formato ISO puro
    const cleanedData = {
      ...formData,
      salario: formData.salario ? formData.salario.replace(/,/g, '') : '',
      // ✅ Enviar fechas directamente (ya están en formato YYYY-MM-DD)
      fecha_nacimiento: formData.fechaNacimiento || null,
      fecha_ingreso: formData.fechaIngreso || null
    };

    // Eliminar versiones duplicadas
    delete cleanedData.fechaNacimiento;
    delete cleanedData.fechaIngreso;
    delete cleanedData.fotoPreview;

    console.log('📤 Datos a enviar:', {
      fecha_nacimiento: cleanedData.fecha_nacimiento,
      fecha_ingreso: cleanedData.fecha_ingreso
    });

    onSave(cleanedData);
  };

  return (
    <div className="bg-white">
      <form onSubmit={handleSubmit}>
        {/* Sección de Foto */}
        <div className="mb-6 p-4 bg-gray-50 rounded-xl">
          <h3 className="text-lg font-bold text-gray-800 mb-3">Foto del Empleado (2x2)</h3>
          <div className="flex items-start gap-4">
            <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white flex items-center justify-center">
              {(formData.fotoPreview || formData.foto) ? (
                <img
                  src={formData.fotoPreview || (typeof formData.foto === 'string' ? formData.foto : '')}
                  alt="Foto empleado"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-center p-4">
                  <User className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">Sin foto</p>
                </div>
              )}
            </div>

            <div className="flex-1">
              <div className="flex gap-2">
                <label className="px-4 py-2 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600 flex items-center gap-2 text-sm">
                  <Upload className="w-4 h-4" />
                  Subir Foto
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>

                {formData.foto && (
                  <button
                    type="button"
                    onClick={handleRemoveFoto}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    Eliminar
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                📌 Recomendado: Foto 2x2 (tipo pasaporte)<br/>
                📏 Tamaño máximo: 2MB<br/>
                📄 Formatos: JPG, PNG, WEBP
              </p>
            </div>
          </div>
        </div>

        {/* Información Personal */}
        <h3 className="text-lg font-bold text-gray-800 mb-3 mt-6">Información Personal</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <input
            type="text"
            name="nombre"
            value={formData.nombre}
            onChange={handleInputChange}
            placeholder="Nombre *"
            className="px-4 py-3 border-2 rounded-xl"
            required
          />
          <input
            type="text"
            name="apellido"
            value={formData.apellido}
            onChange={handleInputChange}
            placeholder="Apellido *"
            className="px-4 py-3 border-2 rounded-xl"
            required
          />
          <input
            type="text"
            name="cedula"
            value={formData.cedula}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '');
              if (v.length <= 11) handleInputChange({ target: { name: 'cedula', value: v } });
            }}
            placeholder="Cédula (11 dígitos) *"
            className="px-4 py-3 border-2 rounded-xl"
            required
          />

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Fecha de Nacimiento
            </label>
            <input
              type="date"
              name="fechaNacimiento"
              value={formData.fechaNacimiento}
              onChange={handleInputChange}
              max="9999-12-31"
              className="w-full px-4 py-3 border-2 rounded-xl"
            />
            {formData.fechaNacimiento && (
              <p className="text-xs text-blue-600 mt-1">
                ✅ {formData.fechaNacimiento}
              </p>
            )}
          </div>

          <input
            type="number"
            name="edad"
            value={formData.edad}
            readOnly
            placeholder="Edad"
            className="px-4 py-3 border-2 rounded-xl bg-gray-50"
          />

          <select
            name="sexo"
            value={formData.sexo}
            onChange={handleInputChange}
            className="px-4 py-3 border-2 rounded-xl"
          >
            <option value="">Sexo</option>
            <option value="Masculino">Masculino</option>
            <option value="Femenino">Femenino</option>
          </select>

          <input
            type="text"
            name="nacionalidad"
            value={formData.nacionalidad}
            onChange={handleInputChange}
            placeholder="Nacionalidad"
            className="px-4 py-3 border-2 rounded-xl"
          />

          <input
            type="text"
            name="pais"
            value={formData.pais}
            onChange={handleInputChange}
            placeholder="País"
            className="px-4 py-3 border-2 rounded-xl"
          />

          <select
            name="tipoSangre"
            value={formData.tipoSangre}
            onChange={handleInputChange}
            className="px-4 py-3 border-2 rounded-xl"
          >
            <option value="">Tipo de Sangre</option>
            {TIPOS_SANGRE.map(tipo => (
              <option key={tipo} value={tipo}>{tipo}</option>
            ))}
          </select>

          <input
            type="text"
            name="profesion"
            value={formData.profesion}
            onChange={handleInputChange}
            placeholder="Profesión"
            className="px-4 py-3 border-2 rounded-xl"
          />

          <input
            type="tel"
            name="telefono"
            value={formData.telefono}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '');
              if (v.length <= 10) handleInputChange({ target: { name: 'telefono', value: v } });
            }}
            placeholder="Teléfono (10 dígitos)"
            className="px-4 py-3 border-2 rounded-xl"
          />

          <input
            type="tel"
            name="celular"
            value={formData.celular}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '');
              if (v.length <= 10) handleInputChange({ target: { name: 'celular', value: v } });
            }}
            placeholder="Celular (10 dígitos)"
            className="px-4 py-3 border-2 rounded-xl"
          />

          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Email"
            className="px-4 py-3 border-2 rounded-xl md:col-span-3"
          />

          <input
            type="text"
            name="nss"
            value={formData.nss}
            onChange={handleInputChange}
            placeholder="Número NSS"
            className="px-4 py-3 border-2 rounded-xl"
          />

          <select
            name="nivelEducativo"
            value={formData.nivelEducativo}
            onChange={handleInputChange}
            className="px-4 py-3 border-2 rounded-xl"
          >
            <option value="">Nivel Educativo</option>
            <option value="primaria">Primaria</option>
            <option value="secundaria">Secundaria</option>
            <option value="tecnico_superior">Técnico Superior</option>
            <option value="estudiante_universitario">Estudiante Universitario</option>
            <option value="grado_universitario">Grado Universitario</option>
            <option value="post_grado">Post Grado</option>
          </select>

          <input
            type="text"
            name="contactoEmergencia"
            value={formData.contactoEmergencia}
            onChange={handleInputChange}
            placeholder="Contacto de Emergencia (Nombre y Tel)"
            className="px-4 py-3 border-2 rounded-xl"
          />
        </div>

        <input
          type="text"
          name="direccion"
          value={formData.direccion}
          onChange={handleInputChange}
          placeholder="Dirección completa"
          className="w-full px-4 py-3 border-2 rounded-xl mb-4"
        />

        {/* Nota sobre Información Laboral */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mt-6 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-blue-900 mb-2">Información Laboral y Contrato</h3>
              <p className="text-sm text-blue-800 mb-3">
                La información laboral (departamento, cargo, salario, fecha de ingreso, etc.) se asigna automáticamente al empleado cuando se crea un <strong>Contrato</strong> desde el módulo de <strong>Gestión de Personal → Contratos</strong>.
              </p>
              <p className="text-xs text-blue-700 bg-blue-100 px-3 py-2 rounded-lg">
                💡 <strong>Flujo recomendado:</strong> Crear empleado → Ir a Contratos → Crear contrato laboral → Se asigna automáticamente la información laboral
              </p>
            </div>
          </div>
        </div>

        {/* Información Bancaria */}
        <h3 className="text-lg font-bold text-gray-800 mb-3 mt-6">Información Bancaria</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <select
            name="nombreBanco"
            value={formData.nombreBanco}
            onChange={handleInputChange}
            className="px-4 py-3 border-2 rounded-xl"
          >
            <option value="">Seleccionar Banco</option>
            {BANCOS_DOMINICANOS.map(banco => (
              <option key={banco} value={banco}>{banco}</option>
            ))}
          </select>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Número de Cuenta
            </label>
            <input
              type="text"
              name="cuentaBanco"
              value={formData.cuentaBanco}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '');
                if (v.length <= 20) handleInputChange({ target: { name: 'cuentaBanco', value: v } });
              }}
              placeholder="10-20 dígitos"
              className="w-full px-4 py-3 border-2 rounded-xl"
            />
            <p className="text-xs text-gray-500 mt-1">Bancos dominicanos: 10-20 dígitos</p>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <button
            type="submit"
            className="px-8 py-4 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700"
          >
            {employee ? 'Actualizar' : 'Guardar'} Empleado
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-8 py-4 bg-gray-300 rounded-xl font-semibold hover:bg-gray-400"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}
